import { useState, useEffect } from 'react';
import {
  RefreshCw,
  Search,
  Settings,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  TrendingDown,
  AlertTriangle,
  Printer
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { StockWithDetails, Location } from '../types';
import Modal from './Modal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoResults, EmptyStateNoData } from './EmptyState';
import LoadingState from './LoadingState';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (minStock === 0) return 'var(--text-primary)';
  if (stock < minStock) {
    return 'var(--error)';
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)';
  } else {
    return 'var(--success)';
  }
};

// Función helper para verificar si mostrar icono de alerta
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  if (minStock === 0) return false;
  return stock < minStock * 0.5;
};

const defaultMetalColumns: Record<string, boolean> = {
  codigo: true,
  nombre: true,
  stock_taller: true,
  stock_listo_pvd: true,
  stock_listo_dlc: true,
  stock_en_proceso_pvd: true,
  stock_en_proceso_dlc: true,
  stock_acabado_plata: true,
  stock_acabado_pvd: true,
  stock_acabado_dlc: true,
};

interface MetalPieceStockSummary {
  id: number;
  cod_pieza: string;
  nombre_pieza: string;
  material_base: 'PLATA' | 'LATON';
  stock_taller: { qty: number; min: number };
  stock_listo_pvd: { qty: number; min: number };
  stock_listo_dlc: { qty: number; min: number };
  stock_en_proceso_pvd: { qty: number; min: number };
  stock_en_proceso_dlc: { qty: number; min: number };
  stock_acabado_plata: { qty: number; min: number };
  stock_acabado_pvd: { qty: number; min: number };
  stock_acabado_dlc: { qty: number; min: number };
  coste_fundicion: number;
  coste_pvd: number;
  coste_dlc: number;
  peso_plata: number;
  stock_otros: number; // Stock en ubicaciones no listadas específicamente
}

export default function StockMetalPieces() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { isAdmin, hasPermission } = useAuth();
  const [metalStockSummary, setMetalStockSummary] = useState<MetalPieceStockSummary[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiplier, setMultiplier] = useState<number>(1);
  const [silverPrice, setSilverPrice] = useState<number>(0.95);
  const [isMultiplierLoaded, setIsMultiplierLoaded] = useState(false);
  const [isSilverPriceLoaded, setIsSilverPriceLoaded] = useState(false);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [selectedPieceTypes, setSelectedPieceTypes] = useState<string[]>(() => {
    const saved = localStorage.getItem('stock_metal_filter_types');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (e) {
        // Si hay error, usar valor por defecto
      }
    }
    return ['PULSERA']; // Por defecto, mostrar Pulsera
  });
  const [searchMetal, setSearchMetal] = useState('');
  const [debouncedSearchMetal, setDebouncedSearchMetal] = useState('');

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchMetal(searchMetal);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchMetal]);

  // Estado para ordenamiento
  const [metalSortConfig, setMetalSortConfig] = useState<{
    column: string | null;
    direction: 'asc' | 'desc';
  }>({
    column: null,
    direction: 'asc',
  });

  // Columnas visibles
  const [visibleMetalColumns, setVisibleMetalColumns] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('stock_metal_columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...defaultMetalColumns, ...parsed };
      } catch (e) {
        // Si hay error, usar valores por defecto
      }
    }
    return { ...defaultMetalColumns };
  });

  const [isMetalColumnsModalOpen, setIsMetalColumnsModalOpen] = useState(false);
  const [isEditStockModalOpen, setIsEditStockModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isEditMinStockModalOpen, setIsEditMinStockModalOpen] = useState(false);
  const [editingStock, setEditingStock] = useState<{
    pieceId: number;
    pieceCode: string;
    pieceName: string;
    locationType: string;
    locationId: number;
    locationName: string;
    currentQty: number;
  } | null>(null);
  const [editingMinStock, setEditingMinStock] = useState<{
    pieceId: number;
    pieceCode: string;
    pieceName: string;
    locationType: string;
    locationId: number | null;
    locationName: string;
    currentMin: number;
  } | null>(null);
  const [editStockForm, setEditStockForm] = useState({ new_qty: '' });
  const [editMinStockForm, setEditMinStockForm] = useState({ new_min: '' });

  // Escuchar eventos de búsqueda global
  useEffect(() => {
    const handleGlobalSearch = (e: CustomEvent) => {
      const { type, query } = e.detail;
      if (type === 'metal') {
        setSearchMetal(query);
      }
    };

    window.addEventListener('global-search-select' as any, handleGlobalSearch as any);
    return () => {
      window.removeEventListener('global-search-select' as any, handleGlobalSearch as any);
    };
  }, []);

  const handleToggleMetalColumn = (column: string) => {
    const newColumns = { ...visibleMetalColumns, [column]: !visibleMetalColumns[column] };
    setVisibleMetalColumns(newColumns);
    localStorage.setItem('stock_metal_columns', JSON.stringify(newColumns));
  };

  const handleMetalSort = (column: string) => {
    setMetalSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return {
          column,
          direction: 'asc',
        };
      }
    });
  };

  const getSortIcon = (column: string) => {
    if (metalSortConfig.column !== column) {
      return <ArrowUpDown size={14} />;
    }
    return metalSortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Función helper para determinar el tipo de pieza basándose en el código
  const getPieceType = (codigo: string): 'ANILLO' | 'COLGANTE' | 'PULSERA' | 'REASA' | null => {
    if (!codigo || typeof codigo !== 'string') return null;

    const cleaned = codigo.trim().toUpperCase();
    if (cleaned.length === 0) return null;

    const firstChar = cleaned.charAt(0);

    if (firstChar === 'A') return 'ANILLO';
    if (firstChar === 'C') return 'COLGANTE';
    if (firstChar === 'P') return 'PULSERA';
    if (firstChar === 'R') return 'REASA';

    return null;
  };

  const togglePieceType = (type: string) => {
    setSelectedPieceTypes((prev) => {
      const newTypes = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      localStorage.setItem('stock_metal_filter_types', JSON.stringify(newTypes));
      return newTypes;
    });
  };

  const sortMetalData = (data: MetalPieceStockSummary[]): MetalPieceStockSummary[] => {
    if (!metalSortConfig.column) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (metalSortConfig.column) {
        case 'codigo':
          aValue = a.cod_pieza.toLowerCase();
          bValue = b.cod_pieza.toLowerCase();
          break;
        case 'nombre':
          aValue = a.nombre_pieza.toLowerCase();
          bValue = b.nombre_pieza.toLowerCase();
          break;
        case 'material':
          aValue = a.material_base;
          bValue = b.material_base;
          break;
        case 'stock_taller':
          aValue = a.stock_taller.qty;
          bValue = b.stock_taller.qty;
          break;
        case 'stock_listo_pvd':
          aValue = a.stock_listo_pvd.qty;
          bValue = b.stock_listo_pvd.qty;
          break;
        case 'stock_listo_dlc':
          aValue = a.stock_listo_dlc.qty;
          bValue = b.stock_listo_dlc.qty;
          break;
        case 'stock_en_proceso_pvd':
          aValue = a.stock_en_proceso_pvd.qty;
          bValue = b.stock_en_proceso_pvd.qty;
          break;
        case 'stock_en_proceso_dlc':
          aValue = a.stock_en_proceso_dlc.qty;
          bValue = b.stock_en_proceso_dlc.qty;
          break;
        case 'stock_acabado_plata':
          aValue = a.stock_acabado_plata.qty;
          bValue = b.stock_acabado_plata.qty;
          break;
        case 'stock_acabado_pvd':
          aValue = a.stock_acabado_pvd.qty;
          bValue = b.stock_acabado_pvd.qty;
          break;
        case 'stock_acabado_dlc':
          aValue = a.stock_acabado_dlc.qty;
          bValue = b.stock_acabado_dlc.qty;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return metalSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return metalSortConfig.direction === 'asc'
          ? (aValue || 0) - (bValue || 0)
          : (bValue || 0) - (aValue || 0);
      }
    });

    return sorted;
  };

  useEffect(() => {
    loadMultiplier();
    loadSilverPrice();
    loadLocations();
  }, []);

  // Escuchar cambios en el multiplicador desde otros componentes
  useEffect(() => {
    const handleMultiplierUpdated = (e: CustomEvent) => {
      const newMultiplier = e.detail?.multiplier;
      if (newMultiplier !== undefined && newMultiplier !== multiplier) {
        setMultiplier(newMultiplier);
        setIsMultiplierLoaded(true);
        // El useEffect que depende de isMultiplierLoaded se encargará de recargar
      }
    };

    window.addEventListener('multiplier-updated' as any, handleMultiplierUpdated as any);
    return () => {
      window.removeEventListener('multiplier-updated' as any, handleMultiplierUpdated as any);
    };
  }, [multiplier]);

  useEffect(() => {
    // Solo cargar stock después de que el multiplicador y precio plata estén cargados
    if (isMultiplierLoaded && isSilverPriceLoaded) {
      loadMetalStock();
    }
  }, [debouncedSearchMetal, isMultiplierLoaded, isSilverPriceLoaded]);

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadMultiplier = async () => {
    try {
      const response = await api.get('/settings/min-stock-multiplier');
      // Bug fix: El backend devuelve { multiplier }, no { value }
      const newMultiplier = parseFloat(response.data.multiplier) || 1;
      setMultiplier(newMultiplier);
      setIsMultiplierLoaded(true);
    } catch (err: any) {
      // Error silencioso - se usa valor por defecto (1)
    }
  };

  const loadSilverPrice = async () => {
    try {
      const response = await api.get('/settings/silver-price');
      const newPrice = parseFloat(response.data.price) || 0.95;
      setSilverPrice(newPrice);
      setIsSilverPriceLoaded(true);
    } catch (err: any) {
      // Error silencioso
      setIsSilverPriceLoaded(true);
    }
  };

  const loadMetalStock = async () => {
    // Protección contra llamadas simultáneas
    if (isLoadingStock) {
      return;
    }
    try {
      setIsLoadingStock(true);
      setLoading(true);

      // Cargar TODAS las piezas activas (sin paginación)
      const metalPiecesResponse = await api.get('/metal-pieces', {
        params: {
          search: debouncedSearchMetal,
          includeInactive: false
        }
      });

      let metalPieces: any[] = Array.isArray(metalPiecesResponse.data)
        ? metalPiecesResponse.data
        : (metalPiecesResponse.data?.data || []);

      if (!Array.isArray(metalPieces)) {
        showError('Error: respuesta del servidor no tiene el formato esperado');
        metalPieces = [];
      }

      // Si no hay piezas, vaciar el resumen y salir
      if (!metalPieces || metalPieces.length === 0) {
        setMetalStockSummary([]);
        setLoading(false);
        return;
      }

      // Obtener IDs de las piezas para filtrar stock
      const metalPieceIds = metalPieces.map((p: any) => p.id);

      // Cargar todas las ubicaciones (si no se han cargado)
      let allLocations = locations;
      if (allLocations.length === 0) {
        const locationsResponse = await api.get('/locations');
        allLocations = locationsResponse.data;
        setLocations(allLocations);
      }

      const tallerPlata = allLocations.find((l: Location) => l.code === 'TALLER_PLATA');
      const tallerLaton = allLocations.find((l: Location) => l.code === 'TALLER_LATON');
      const listoPvd = allLocations.find((l: Location) => l.code === 'LISTO_PVD');
      const listoDlc = allLocations.find((l: Location) => l.code === 'LISTO_DLC');
      const enProcesoPvd = allLocations.find((l: Location) => l.code === 'EN_PROCESO_PVD');
      const enProcesoDlc = allLocations.find((l: Location) => l.code === 'EN_PROCESO_DLC');
      const acabadoPlata = allLocations.find((l: Location) => l.code === 'ACABADO_PLATA');
      const acabadoPvd = allLocations.find((l: Location) => l.code === 'ACABADO_PVD');
      const acabadoDlc = allLocations.find((l: Location) => l.code === 'ACABADO_DLC');

      // Cargar stock de piezas metálicas para los IDs obtenidos
      const metalStockParams: any = {
        item_type: 'METAL',
        item_ids: metalPieceIds
      };
      const metalStockResponse = await api.get('/stock', { params: metalStockParams });
      const metalStock = metalStockResponse.data;

      // Cargar mínimos de stock
      const minStockResponse = await api.get('/min-stock', { params: { item_type: 'METAL' } });
      const minStocks = minStockResponse.data;

      const acabadosIds = [acabadoPlata?.id, acabadoPvd?.id, acabadoDlc?.id].filter(id => id !== undefined) as number[];
      const coatingLocationsIds = [listoPvd?.id, listoDlc?.id, enProcesoPvd?.id, enProcesoDlc?.id, acabadoPvd?.id, acabadoDlc?.id].filter(id => id !== undefined) as number[];

      // Agrupar stock por pieza metálica
      const summary: MetalPieceStockSummary[] = metalPieces.map((piece: any) => {
        const getStock = (locationId?: number) => {
          if (!locationId) return 0;
          const stock = metalStock.find(
            (s: StockWithDetails) => s.item_id === piece.id && s.location_id === locationId
          );
          return stock?.qty || 0;
        };

        const getMin = (locationId?: number) => {
          if (!locationId) return 0;
          const minStock = minStocks.find(
            (m: any) => m.item_id === piece.id && m.location_id === locationId
          );
          if (!minStock) return 0;
          return Math.round(minStock.min_qty_base * multiplier);
        };

        const locationIdTaller = piece.material_base === 'PLATA' ? tallerPlata?.id : tallerLaton?.id;

        // Calcular stock en ubicaciones "otros" (cualquier stock que no esté en las columnas principales)
        const pieceStockEntries = metalStock.filter((s: StockWithDetails) => s.item_id === piece.id);
        const knownLocationIds = [
          locationIdTaller,
          listoPvd?.id, listoDlc?.id,
          enProcesoPvd?.id, enProcesoDlc?.id,
          acabadoPlata?.id, acabadoPvd?.id, acabadoDlc?.id
        ].filter(id => id !== undefined);

        const stockOtros = pieceStockEntries
          .filter((s: StockWithDetails) => !knownLocationIds.includes(s.location_id))
          .reduce((sum: number, s: StockWithDetails) => sum + (s.qty || 0), 0);

        return {
          id: piece.id,
          cod_pieza: piece.cod_pieza,
          nombre_pieza: piece.nombre_pieza,
          material_base: piece.material_base,
          stock_taller: {
            qty: getStock(locationIdTaller),
            min: getMin(locationIdTaller),
          },
          stock_listo_pvd: {
            qty: getStock(listoPvd?.id),
            min: getMin(listoPvd?.id),
          },
          stock_listo_dlc: {
            qty: getStock(listoDlc?.id),
            min: getMin(listoDlc?.id),
          },
          stock_en_proceso_pvd: {
            qty: getStock(enProcesoPvd?.id),
            min: getMin(enProcesoPvd?.id),
          },
          stock_en_proceso_dlc: {
            qty: getStock(enProcesoDlc?.id),
            min: getMin(enProcesoDlc?.id),
          },
          stock_acabado_plata: {
            qty: getStock(acabadoPlata?.id),
            min: getMin(acabadoPlata?.id),
          },
          stock_acabado_pvd: {
            qty: getStock(acabadoPvd?.id),
            min: getMin(acabadoPvd?.id),
          },
          stock_acabado_dlc: {
            qty: getStock(acabadoDlc?.id),
            min: getMin(acabadoDlc?.id),
          },
          coste_fundicion: piece.coste_fundicion || 0,
          coste_pvd: piece.coste_pvd || 0,
          coste_dlc: piece.coste_dlc || 0,
          peso_plata: piece.peso_plata || 0,
          stock_otros: stockOtros,
        };
      });

      setMetalStockSummary(summary);
    } catch (err: any) {
      // Manejo de errores más específico
      const status = err.response?.status;
      const errorMessage = err.response?.data?.error || 'Error al cargar stock de metales';

      if (status === 403) {
        showError(`Sin permisos: ${errorMessage}. Contacta al administrador para obtener acceso a la sección de Stock Piezas.`);
      } else if (status === 401) {
        showError('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        showError(errorMessage);
      }

      // En caso de error, vaciar el resumen para evitar mostrar datos incorrectos
      setMetalStockSummary([]);
    } finally {
      setLoading(false);
      setIsLoadingStock(false);
    }
  };

  const getLocationId = (locationType: string, materialBase: 'PLATA' | 'LATON'): number | undefined => {
    const location = locations.find((l: Location) => {
      if (locationType === 'taller') {
        return materialBase === 'PLATA' ? l.code === 'TALLER_PLATA' : l.code === 'TALLER_LATON';
      } else if (locationType === 'listo_pvd') return l.code === 'LISTO_PVD';
      else if (locationType === 'listo_dlc') return l.code === 'LISTO_DLC';
      else if (locationType === 'proceso_pvd') return l.code === 'EN_PROCESO_PVD';
      else if (locationType === 'proceso_dlc') return l.code === 'EN_PROCESO_DLC';
      else if (locationType === 'acabado_plata') return l.code === 'ACABADO_PLATA';
      else if (locationType === 'acabado_pvd') return l.code === 'ACABADO_PVD';
      else if (locationType === 'acabado_dlc') return l.code === 'ACABADO_DLC';
      return false;
    });
    return location?.id;
  };

  const getLocationName = (locationType: string): string => {
    const names: Record<string, string> = {
      taller: 'Taller',
      listo_pvd: 'Listo PVD',
      listo_dlc: 'Listo DLC',
      proceso_pvd: 'Proc. PVD',
      proceso_dlc: 'Proc. DLC',
      acabado_plata: 'PLATA',
      acabado_pvd: 'PVD',
      acabado_dlc: 'DLC',
    };
    return names[locationType] || locationType;
  };

  const handleEditStock = (
    pieceId: number,
    pieceCode: string,
    pieceName: string,
    locationType: string,
    materialBase: 'PLATA' | 'LATON',
    currentQty: number
  ) => {
    const locationId = getLocationId(locationType, materialBase);
    if (!locationId) return;

    const location = locations.find((l: Location) => l.id === locationId);
    setEditingStock({
      pieceId,
      pieceCode,
      pieceName,
      locationType,
      locationId,
      locationName: location?.nombre || getLocationName(locationType),
      currentQty,
    });
    setEditStockForm({ new_qty: currentQty.toString() });
    setIsEditStockModalOpen(true);
  };

  const handleEditMinStock = (
    pieceId: number,
    pieceCode: string,
    pieceName: string,
    locationType: string,
    materialBase: 'PLATA' | 'LATON',
    currentMin: number
  ) => {
    const locationId = getLocationId(locationType, materialBase);
    if (!locationId) return;

    const location = locations.find((l: Location) => l.id === locationId);
    setEditingMinStock({
      pieceId,
      pieceCode,
      pieceName,
      locationType,
      locationId,
      locationName: location?.nombre || getLocationName(locationType),
      currentMin,
    });
    setEditMinStockForm({ new_min: currentMin.toString() });
    setIsEditMinStockModalOpen(true);
  };

  const handleSaveStock = async () => {
    if (!editingStock) return;

    try {
      const newQty = parseFloat(editStockForm.new_qty);
      if (isNaN(newQty) || newQty < 0) {
        showError('La cantidad debe ser un número mayor o igual a 0');
        return;
      }

      const response = await api.post('/settings/adjust-stock', {
        item_type: 'METAL',
        item_id: editingStock.pieceId,
        location_id: editingStock.locationId,
        new_qty: newQty,
      });

      showSuccess(`Stock ajustado correctamente. Anterior: ${response.data.previous_qty}, Nuevo: ${response.data.new_qty}`);
      setIsEditStockModalOpen(false);
      setEditingStock(null);
      setEditStockForm({ new_qty: '' });

      await loadMetalStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al ajustar stock');
    }
  };

  const handleSaveMinStock = async () => {
    if (!editingMinStock) return;

    try {
      const newMin = parseFloat(editMinStockForm.new_min);
      if (isNaN(newMin) || newMin < 0) {
        showError('El mínimo debe ser un número mayor o igual a 0');
        return;
      }

      await api.post('/settings/adjust-min-stock', {
        item_type: 'METAL',
        item_id: editingMinStock.pieceId,
        location_id: editingMinStock.locationId,
        min_qty: newMin,
      });

      showSuccess(`Mínimo actualizado correctamente. Nuevo valor: ${newMin}`);
      setIsEditMinStockModalOpen(false);
      setEditingMinStock(null);
      setEditMinStockForm({ new_min: '' });
      await loadMetalStock();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al ajustar mínimo');
    }
  };

  // Función para imprimir la lista de stock
  const handlePrintStock = async (includeQuantities: boolean) => {
    try {
      setIsPrintModalOpen(false);
      showSuccess('Cargando datos para imprimir...');

      // Usar los datos ya cargados (metalStockSummary ya tiene todas las piezas filtradas)
      const allPieces = metalStockSummary;

      if (allPieces.length === 0) {
        showError('No hay piezas para imprimir');
        return;
      }

      // Ordenar las piezas
      const sortedPieces = sortMetalData(allPieces);

      // Generar HTML para imprimir
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showError('No se pudo abrir la ventana de impresión. Por favor, permite ventanas emergentes.');
        return;
      }

      // Obtener nombres de columnas visibles
      // Si es plantilla vacía, excluir LISTO PVD y LISTO DLC
      const columnHeaders: Array<{ key: string; label: string }> = [];
      if (visibleMetalColumns.codigo) columnHeaders.push({ key: 'codigo', label: 'CÓDIGO' });
      if (visibleMetalColumns.nombre) columnHeaders.push({ key: 'nombre', label: 'NOMBRE' });
      if (visibleMetalColumns.stock_taller) columnHeaders.push({ key: 'stock_taller', label: 'TALLER' });
      if (includeQuantities) {
        // Solo incluir LISTO PVD y LISTO DLC si es con cantidades
        if (visibleMetalColumns.stock_listo_pvd) columnHeaders.push({ key: 'stock_listo_pvd', label: 'LISTO PVD' });
        if (visibleMetalColumns.stock_listo_dlc) columnHeaders.push({ key: 'stock_listo_dlc', label: 'LISTO DLC' });
      }
      if (visibleMetalColumns.stock_en_proceso_pvd) columnHeaders.push({ key: 'stock_en_proceso_pvd', label: 'PROC. PVD' });
      if (visibleMetalColumns.stock_en_proceso_dlc) columnHeaders.push({ key: 'stock_en_proceso_dlc', label: 'PROC. DLC' });
      if (visibleMetalColumns.stock_acabado_plata) columnHeaders.push({ key: 'stock_acabado_plata', label: 'PLATA' });
      if (visibleMetalColumns.stock_acabado_pvd) columnHeaders.push({ key: 'stock_acabado_pvd', label: 'PVD' });
      if (visibleMetalColumns.stock_acabado_dlc) columnHeaders.push({ key: 'stock_acabado_dlc', label: 'DLC' });

      const getStockValue = (piece: MetalPieceStockSummary, key: string): string => {
        if (!includeQuantities) return '';

        let stockData: { qty: number; min: number };

        switch (key) {
          case 'stock_taller':
            stockData = piece.stock_taller;
            break;
          case 'stock_listo_pvd':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_listo_pvd;
            break;
          case 'stock_listo_dlc':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_listo_dlc;
            break;
          case 'stock_en_proceso_pvd':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_en_proceso_pvd;
            break;
          case 'stock_en_proceso_dlc':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_en_proceso_dlc;
            break;
          case 'stock_acabado_plata':
            if (piece.material_base === 'LATON') return '';
            stockData = piece.stock_acabado_plata;
            break;
          case 'stock_acabado_pvd':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_acabado_pvd;
            break;
          case 'stock_acabado_dlc':
            if (piece.material_base === 'PLATA') return '';
            stockData = piece.stock_acabado_dlc;
            break;
          default:
            return '';
        }

        if (stockData.qty === 0 && stockData.min === 0) {
          return '0 (s/m)';
        }

        const minText = stockData.min > 0 ? ' (' + stockData.min + ')' : '';
        return stockData.qty.toString() + minText;
      };

      const pieceTypeLabel = selectedPieceTypes.length === 0
        ? 'Todas'
        : selectedPieceTypes.length === 4
          ? 'Todas'
          : selectedPieceTypes.join(', ');

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Stock Piezas - ${includeQuantities ? 'Con Cantidades' : 'Plantilla Vacía'}</title>
          <style>
            @page {
              size: A4;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              margin: 0;
              padding: 0;
            }
            h1 {
              text-align: center;
              font-size: 18px;
              margin-bottom: 10px;
            }
            .info {
              text-align: center;
              font-size: 9px;
              color: #666;
              margin-bottom: 15px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-top: 10px;
            }
            th {
              background-color: #f0f0f0;
              border: 1px solid #000;
              padding: 6px 4px;
              text-align: center;
              font-weight: bold;
              font-size: 9px;
            }
            td {
              border: 1px solid #000;
              padding: 5px 4px;
              text-align: left;
              font-size: 9px;
            }
            td:first-child,
            td:nth-child(2) {
              font-weight: bold;
            }
            td[data-align="center"] {
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              body {
                margin: 0;
              }
              table {
                page-break-inside: auto;
              }
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              thead {
                display: table-header-group;
              }
              tfoot {
                display: table-footer-group;
              }
            }
          </style>
        </head>
        <body>
          <h1>STOCK DE PIEZAS METÁLICAS</h1>
          <div class="info">
            ${includeQuantities ? 'Con Cantidades' : 'Plantilla Vacía'} | 
            Tipo: ${pieceTypeLabel} | 
            Fecha: ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })} | 
            Total de piezas: ${sortedPieces.length}
          </div>
          <table>
            <thead>
              <tr>
                ${columnHeaders.map(h => '<th>' + h.label + '</th>').join('')}
              </tr>
            </thead>
            <tbody>
              ${sortedPieces.map(piece => {
        const row = [];
        if (visibleMetalColumns.codigo) row.push('<td><strong>' + piece.cod_pieza + '</strong></td>');
        if (visibleMetalColumns.nombre) row.push('<td>' + piece.nombre_pieza + '</td>');
        if (visibleMetalColumns.stock_taller) row.push('<td data-align="center">' + getStockValue(piece, 'stock_taller') + '</td>');
        // Solo incluir LISTO PVD y LISTO DLC si es con cantidades
        if (includeQuantities) {
          if (visibleMetalColumns.stock_listo_pvd) row.push('<td data-align="center">' + getStockValue(piece, 'stock_listo_pvd') + '</td>');
          if (visibleMetalColumns.stock_listo_dlc) row.push('<td data-align="center">' + getStockValue(piece, 'stock_listo_dlc') + '</td>');
        }
        if (visibleMetalColumns.stock_en_proceso_pvd) row.push('<td data-align="center">' + getStockValue(piece, 'stock_en_proceso_pvd') + '</td>');
        if (visibleMetalColumns.stock_en_proceso_dlc) row.push('<td data-align="center">' + getStockValue(piece, 'stock_en_proceso_dlc') + '</td>');
        if (visibleMetalColumns.stock_acabado_plata) row.push('<td data-align="center">' + getStockValue(piece, 'stock_acabado_plata') + '</td>');
        if (visibleMetalColumns.stock_acabado_pvd) row.push('<td data-align="center">' + getStockValue(piece, 'stock_acabado_pvd') + '</td>');
        if (visibleMetalColumns.stock_acabado_dlc) row.push('<td data-align="center">' + getStockValue(piece, 'stock_acabado_dlc') + '</td>');
        return '<tr>' + row.join('') + '</tr>';
      }).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();

      // Esperar a que se cargue el contenido y luego imprimir
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };

      showSuccess('Ventana de impresión abierta');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al imprimir');
    }
  };

  // Componente para mostrar stock con edición
  const StockDisplay = ({
    stock,
    materialBase,
    locationType,
    pieceId,
    pieceCode,
    pieceName,
  }: {
    stock: { qty: number; min: number };
    materialBase: 'PLATA' | 'LATON';
    locationType: 'taller' | 'listo_pvd' | 'listo_dlc' | 'proceso_pvd' | 'proceso_dlc' | 'acabado_plata' | 'acabado_pvd' | 'acabado_dlc';
    pieceId: number;
    pieceCode: string;
    pieceName: string;
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Piezas de PLATA nunca están en LISTO_PVD, LISTO_DLC, ACABADO_PVD, ACABADO_DLC
    if (
      materialBase === 'PLATA' &&
      (locationType === 'listo_pvd' ||
        locationType === 'listo_dlc' ||
        locationType === 'proceso_pvd' ||
        locationType === 'proceso_dlc' ||
        locationType === 'acabado_pvd' ||
        locationType === 'acabado_dlc')
    ) {
      return null;
    }

    // Piezas de LATON nunca están en ACABADO_PLATA
    if (materialBase === 'LATON' && locationType === 'acabado_plata') {
      return null;
    }

    const iconButtonStyle: React.CSSProperties = {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0.1rem',
      lineHeight: 1,
      fontSize: '0.9rem',
    };

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          position: 'relative',
          padding: '0.25rem 2rem 0.25rem 0.5rem',
          borderRadius: '4px',
          cursor: 'pointer',
          backgroundColor: isHovered ? 'var(--bg-secondary)' : 'transparent',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => handleEditStock(pieceId, pieceCode, pieceName, locationType, materialBase, stock.qty)}
        title="Clic para editar stock"
      >
        {(stock.qty === 0 && stock.min > 0) || (stock.qty < stock.min && stock.min > 0) ? (
          <span style={{ color: 'var(--error)', marginRight: '0.25rem', fontSize: '0.875rem', fontWeight: 'bold' }}>▲</span>
        ) : null}
        <strong style={{ color: getStockColor(stock.qty, stock.min) }}>
          {stock.qty}
        </strong>
        {stock.min > 0 && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
            ({stock.min})
          </span>
        )}
        {stock.qty === 0 && stock.min === 0 && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
            (s/m)
          </span>
        )}
        {shouldShowAlert(stock.qty, stock.min) && stock.qty > 0 && (
          <span style={{ marginLeft: '0.25rem', display: 'flex', alignItems: 'center' }} title="Stock crítico: menos del 50% del mínimo">
            <AlertTriangle size={16} color="var(--error)" />
          </span>
        )}
        <span
          style={{
            position: 'absolute',
            right: '0.35rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '0.25rem',
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? 'auto' : 'none',
            transition: 'opacity 0.2s',
          }}
        >
          <button
            style={iconButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleEditStock(pieceId, pieceCode, pieceName, locationType, materialBase, stock.qty);
            }}
            title="Editar stock"
          >
            <Edit2 size={14} />
          </button>
          <button
            style={iconButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleEditMinStock(pieceId, pieceCode, pieceName, locationType, materialBase, stock.min);
            }}
            title="Editar mínimo"
          >
            <TrendingDown size={14} />
          </button>
        </span>
      </span>
    );
  };

  const getStockDisplay = (
    stock: { qty: number; min: number },
    materialBase: 'PLATA' | 'LATON',
    locationType: 'taller' | 'listo_pvd' | 'listo_dlc' | 'proceso_pvd' | 'proceso_dlc' | 'acabado_plata' | 'acabado_pvd' | 'acabado_dlc',
    pieceId: number,
    pieceCode: string,
    pieceName: string
  ) => {
    return (
      <StockDisplay
        stock={stock}
        materialBase={materialBase}
        locationType={locationType}
        pieceId={pieceId}
        pieceCode={pieceCode}
        pieceName={pieceName}
      />
    );
  };

  // Filtrar por búsqueda y tipo de pieza
  const filteredMetal = metalStockSummary.filter((piece) => {
    // Filtro por tipo de pieza (checkboxes - múltiples seleccionados)
    if (selectedPieceTypes.length > 0) {
      const pieceType = getPieceType(piece.cod_pieza);
      if (!pieceType || !selectedPieceTypes.includes(pieceType)) {
        return false;
      }
    }

    // Filtro por búsqueda
    if (!debouncedSearchMetal.trim()) return true;
    const search = debouncedSearchMetal.toLowerCase().trim();
    return (
      piece.cod_pieza.toLowerCase().includes(search) ||
      piece.nombre_pieza.toLowerCase().includes(search)
    );
  });

  // Aplicar ordenamiento
  const sortedMetal = sortMetalData(filteredMetal);

  if (loading && metalStockSummary.length === 0) {
    return (
      <div className="tab-content">
        <LoadingState type="table" message="Cargando stock de piezas..." />
      </div>
    );
  }

  return (
    <div className="tab-content">
      {/* Filtros por tipo de pieza */}
      <div className="table-container" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.125rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              FILTRAR POR TIPO DE PIEZA
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'ANILLO', label: 'Anillo', color: '#3b82f6' },
                { value: 'COLGANTE', label: 'Colgante', color: '#a855f7' },
                { value: 'PULSERA', label: 'Pulsera', color: '#10b981' },
                { value: 'REASA', label: 'Reasa', color: '#f59e0b' },
              ].map((type) => {
                const isSelected = selectedPieceTypes.includes(type.value);
                return (
                  <label
                    key={type.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${type.color}15` : 'transparent',
                      color: isSelected ? type.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${type.color}20` : 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="pieceTypeFilter"
                      value={type.value}
                      checked={isSelected}
                      onChange={() => togglePieceType(type.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      border: `2px solid ${isSelected ? type.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? type.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}>
                      {isSelected && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ position: 'absolute' }}
                        >
                          <path
                            d="M2 5L4 7L8 3"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span>{type.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <input
              type="text"
              className="input"
              placeholder="Buscar por código o nombre..."
              value={searchMetal}
              onChange={(e) => setSearchMetal(e.target.value)}
              style={{ minWidth: '250px' }}
            />
            <button
              className="btn btn-secondary"
              onClick={() => setIsPrintModalOpen(true)}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Imprimir lista de stock"
            >
              <Printer size={16} /> Imprimir
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setIsMetalColumnsModalOpen(true)}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Personalizar columnas"
            >
              <Settings size={16} /> Columnas
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => loadMetalStock()}
              style={{ padding: '0.5rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Actualizar stock"
            >
              <RefreshCw size={16} /> Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de Stock */}
      <div>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', minWidth: '1200px' }}>
            <thead>
              <tr>
                {visibleMetalColumns.codigo && (
                  <th
                    className="sticky-col"
                    style={{
                      minWidth: '120px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      paddingRight: '20px'
                    }}
                    onClick={() => handleMetalSort('codigo')}
                    title="Clic para ordenar"
                  >
                    Código <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('codigo')}</span>
                  </th>
                )}
                {visibleMetalColumns.nombre && (
                  <th
                    className="sticky-col"
                    style={{
                      minWidth: '250px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      paddingRight: '20px'
                    }}
                    onClick={() => handleMetalSort('nombre')}
                    title="Clic para ordenar"
                  >
                    Nombre <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('nombre')}</span>
                  </th>
                )}
                {visibleMetalColumns.stock_taller && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_taller.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '120px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_taller')}
                      title="Clic para ordenar"
                    >
                      <div>Taller <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_taller')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_listo_pvd && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_listo_pvd.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '120px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_listo_pvd')}
                      title="Clic para ordenar"
                    >
                      <div>Listo PVD <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_listo_pvd')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_listo_dlc && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_listo_dlc.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '120px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_listo_dlc')}
                      title="Clic para ordenar"
                    >
                      <div>Listo DLC <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_listo_dlc')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_en_proceso_pvd && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_en_proceso_pvd.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '140px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_en_proceso_pvd')}
                      title="Clic para ordenar"
                    >
                      <div>Proc. PVD <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_en_proceso_pvd')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_en_proceso_dlc && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_en_proceso_dlc.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '140px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_en_proceso_dlc')}
                      title="Clic para ordenar"
                    >
                      <div>Proc. DLC <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_en_proceso_dlc')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_acabado_plata && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_acabado_plata.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '140px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_acabado_plata')}
                      title="Clic para ordenar"
                    >
                      <div>PLATA <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_acabado_plata')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_acabado_pvd && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_acabado_pvd.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '120px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_acabado_pvd')}
                      title="Clic para ordenar"
                    >
                      <div>PVD <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_acabado_pvd')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
                {visibleMetalColumns.stock_acabado_dlc && (() => {
                  const total = sortedMetal.reduce((sum, p) => sum + p.stock_acabado_dlc.qty, 0);
                  return (
                    <th
                      style={{
                        minWidth: '120px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        paddingRight: '20px'
                      }}
                      onClick={() => handleMetalSort('stock_acabado_dlc')}
                      title="Clic para ordenar"
                    >
                      <div>DLC <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>{getSortIcon('stock_acabado_dlc')}</span></div>
                      <div style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--text-primary)', marginTop: '4px' }}>{Math.round(total).toLocaleString()}</div>
                    </th>
                  );
                })()}
              </tr>
            </thead>
            <tbody>
              {sortedMetal.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      Object.values(visibleMetalColumns).filter(v => v).length ||
                      Object.keys(defaultMetalColumns).length
                    }
                    className="text-center p-0"
                  >
                    {searchMetal ? (
                      <EmptyStateNoResults
                        onClearFilters={() => setSearchMetal('')}
                      />
                    ) : (
                      <EmptyStateNoData />
                    )}
                  </td>
                </tr>
              ) : (
                <>
                  {sortedMetal.map((piece) => (
                    <tr key={piece.id}>
                      {visibleMetalColumns.codigo && (
                        <td className="sticky-col" data-label="Código">
                          <strong
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                              color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                            }}
                          >
                            {piece.cod_pieza}
                          </strong>
                        </td>
                      )}
                      {visibleMetalColumns.nombre && (
                        <td className="sticky-col" data-label="Nombre">
                          {piece.nombre_pieza}
                          {piece.stock_otros > 0 && (
                            <span
                              style={{
                                marginLeft: '0.5rem',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                backgroundColor: 'var(--bg-tertiary)',
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)',
                                cursor: 'help'
                              }}
                              title={`Hay ${piece.stock_otros} unidades en otras ubicaciones no listadas aquí`}
                            >
                              +{piece.stock_otros} otros
                            </span>
                          )}
                        </td>
                      )}
                      {visibleMetalColumns.stock_taller && <td style={{ textAlign: 'center' }} data-label="Taller">{getStockDisplay(piece.stock_taller, piece.material_base, 'taller', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_listo_pvd && <td style={{ textAlign: 'center' }} data-label="Listo PVD">{getStockDisplay(piece.stock_listo_pvd, piece.material_base, 'listo_pvd', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_listo_dlc && <td style={{ textAlign: 'center' }} data-label="Listo DLC">{getStockDisplay(piece.stock_listo_dlc, piece.material_base, 'listo_dlc', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_en_proceso_pvd && <td style={{ textAlign: 'center' }} data-label="Proc. PVD">{getStockDisplay(piece.stock_en_proceso_pvd, piece.material_base, 'proceso_pvd', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_en_proceso_dlc && <td style={{ textAlign: 'center' }} data-label="Proc. DLC">{getStockDisplay(piece.stock_en_proceso_dlc, piece.material_base, 'proceso_dlc', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_acabado_plata && <td style={{ textAlign: 'center' }} data-label="PLATA">{getStockDisplay(piece.stock_acabado_plata, piece.material_base, 'acabado_plata', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_acabado_pvd && <td style={{ textAlign: 'center' }} data-label="PVD">{getStockDisplay(piece.stock_acabado_pvd, piece.material_base, 'acabado_pvd', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                      {visibleMetalColumns.stock_acabado_dlc && <td style={{ textAlign: 'center' }} data-label="DLC">{getStockDisplay(piece.stock_acabado_dlc, piece.material_base, 'acabado_dlc', piece.id, piece.cod_pieza, piece.nombre_pieza)}</td>}
                    </tr>
                  ))}
                  {/* Fila de totales */}
                  <tr key="totals-row" style={{ backgroundColor: 'var(--bg-secondary)', fontWeight: 'bold', borderTop: '2px solid var(--border)' }}>
                    {visibleMetalColumns.codigo && <td className="sticky-col" style={{ borderTop: '2px solid var(--border)' }}></td>}
                    {visibleMetalColumns.nombre && <td className="sticky-col" style={{ textAlign: 'right', borderTop: '2px solid var(--border)', paddingRight: '1rem' }}><strong>TOTAL:</strong></td>}
                    {visibleMetalColumns.material && <td style={{ borderTop: '2px solid var(--border)' }}></td>}
                    {visibleMetalColumns.stock_taller && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_taller.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_listo_pvd && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_listo_pvd.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_listo_dlc && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_listo_dlc.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_en_proceso_pvd && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_en_proceso_pvd.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_en_proceso_dlc && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_en_proceso_dlc.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_acabado_plata && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_acabado_plata.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_acabado_pvd && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_acabado_pvd.qty, 0)).toLocaleString()}</td>}
                    {visibleMetalColumns.stock_acabado_dlc && <td style={{ textAlign: 'center', fontSize: '1.1em', borderTop: '2px solid var(--border)', fontWeight: 'bold' }}>{Math.round(sortedMetal.reduce((sum, p) => sum + p.stock_acabado_dlc.qty, 0)).toLocaleString()}</td>}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Panel de Resumen de Valoración */}
        {sortedMetal.length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1.25rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            {(() => {
              const valuation = sortedMetal.reduce((acc, p) => {
                const totalQty = p.stock_taller.qty + p.stock_listo_pvd.qty + p.stock_listo_dlc.qty +
                  p.stock_en_proceso_pvd.qty + p.stock_en_proceso_dlc.qty +
                  p.stock_acabado_plata.qty + p.stock_acabado_pvd.qty + p.stock_acabado_dlc.qty +
                  p.stock_otros;

                const pvdQty = p.stock_listo_pvd.qty + p.stock_en_proceso_pvd.qty + p.stock_acabado_pvd.qty;
                const dlcQty = p.stock_listo_dlc.qty + p.stock_en_proceso_dlc.qty + p.stock_acabado_dlc.qty;

                if (p.material_base === 'LATON') {
                  acc.fundicionLaton += totalQty * p.coste_fundicion;
                  acc.pvd += pvdQty * p.coste_pvd;
                  acc.dlc += dlcQty * p.coste_dlc;
                  // Si hay stock_otros en latón, se valora solo fundición (procedimiento conservador)
                } else if (p.material_base === 'PLATA') {
                  acc.fundicionPlata += totalQty * p.coste_fundicion;
                  if (p.peso_plata > 0) {
                    acc.pesoPlata += totalQty * p.peso_plata;
                  }
                }

                return acc;
              }, { fundicionLaton: 0, fundicionPlata: 0, pvd: 0, dlc: 0, pesoPlata: 0 });

              const valorPlataPeso = valuation.pesoPlata * silverPrice;
              const valorLatonTotal = valuation.fundicionLaton + valuation.pvd + valuation.dlc;
              const valorPlataTotal = valuation.fundicionPlata + valorPlataPeso;
              const totalGlobal = valorLatonTotal + valorPlataTotal;

              const formatCurrency = (amount: number) =>
                new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);

              const handleUpdateSilverPrice = async () => {
                const newPrice = prompt('Ingresa la nueva cotización de plata (€/g):', silverPrice.toString());
                if (newPrice !== null) {
                  const parsed = parseFloat(newPrice);
                  if (!isNaN(parsed) && parsed >= 0) {
                    try {
                      await api.put('/settings/silver-price', { price: parsed });
                      setSilverPrice(parsed);
                      showSuccess('Cotización de plata actualizada');
                    } catch (err) {
                      showError('Error al actualizar cotización');
                    }
                  }
                }
              };

              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Valor Piezas Latón</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }} title="Fundición Latón + PVD + DLC">
                      {formatCurrency(valorLatonTotal)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Valor Piezas Plata</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }} title="Fundición Plata + Valor Plata Peso">
                      {formatCurrency(valorPlataTotal)}
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', cursor: 'pointer' }}
                    onClick={handleUpdateSilverPrice}
                    title="Hacer clic para actualizar cotización"
                  >
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Valor Plata Peso ({silverPrice} €/g) <Edit2 size={12} />
                    </span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{formatCurrency(valorPlataPeso)}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', borderLeft: '2px solid var(--border)', paddingLeft: '1.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Valor Total Stock</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{formatCurrency(totalGlobal)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Modal de configuración de columnas */}
      <Modal
        isOpen={isMetalColumnsModalOpen}
        onClose={() => setIsMetalColumnsModalOpen(false)}
        title="Personalizar Columnas - Piezas Metálicas"
        footer={
          <button className="btn btn-primary" onClick={() => setIsMetalColumnsModalOpen(false)}>
            Cerrar
          </button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.codigo ?? true}
                onChange={() => handleToggleMetalColumn('codigo')}
              />
              <span>Código</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.nombre ?? true}
                onChange={() => handleToggleMetalColumn('nombre')}
              />
              <span>Nombre</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.material ?? true}
                onChange={() => handleToggleMetalColumn('material')}
              />
              <span>Material</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_taller ?? true}
                onChange={() => handleToggleMetalColumn('stock_taller')}
              />
              <span>Taller</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_listo_pvd ?? true}
                onChange={() => handleToggleMetalColumn('stock_listo_pvd')}
              />
              <span>Listo PVD</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_listo_dlc ?? true}
                onChange={() => handleToggleMetalColumn('stock_listo_dlc')}
              />
              <span>Listo DLC</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_en_proceso_pvd ?? true}
                onChange={() => handleToggleMetalColumn('stock_en_proceso_pvd')}
              />
              <span>Proc. PVD</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_en_proceso_dlc ?? true}
                onChange={() => handleToggleMetalColumn('stock_en_proceso_dlc')}
              />
              <span>Proc. DLC</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_acabado_plata ?? true}
                onChange={() => handleToggleMetalColumn('stock_acabado_plata')}
              />
              <span>PLATA</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_acabado_pvd ?? true}
                onChange={() => handleToggleMetalColumn('stock_acabado_pvd')}
              />
              <span>PVD</span>
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
              <input
                type="checkbox"
                checked={visibleMetalColumns.stock_acabado_dlc ?? true}
                onChange={() => handleToggleMetalColumn('stock_acabado_dlc')}
              />
              <span>DLC</span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Modal Editar Stock */}
      <Modal
        isOpen={isEditStockModalOpen}
        onClose={() => {
          setIsEditStockModalOpen(false);
          setEditingStock(null);
          setEditStockForm({ new_qty: '' });
        }}
        title="Ajustar Stock Manualmente"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditStockModalOpen(false);
                setEditingStock(null);
                setEditStockForm({ new_qty: '' });
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveStock}
              data-modal-default="true"
            >
              Guardar
            </button>
          </>
        }
      >
        {editingStock && (
          <>
            <div className="input-group">
              <label className="input-label">Pieza</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingStock.pieceCode}</strong> - {editingStock.pieceName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Ubicación</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                {editingStock.locationName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Stock Actual</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingStock.currentQty}</strong> unidades
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Nueva Cantidad *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input"
                value={editStockForm.new_qty}
                onChange={(e) => setEditStockForm({ ...editStockForm, new_qty: e.target.value })}
                placeholder={`Actual: ${editingStock.currentQty}`}
                required
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Establece la cantidad exacta de stock que deseas tener en esta ubicación
              </small>
            </div>

            {!isAdmin && !hasPermission('STOCK', 'adjust_stock') && (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 59, 48, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 59, 48, 0.3)' }}>
                <strong style={{ color: 'var(--error)' }}>Advertencia:</strong>
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Solo los administradores pueden ajustar stock manualmente.
                </p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Modal Editar Mínimo */}
      <Modal
        isOpen={isEditMinStockModalOpen}
        onClose={() => {
          setIsEditMinStockModalOpen(false);
          setEditingMinStock(null);
          setEditMinStockForm({ new_min: '' });
        }}
        title="Editar Stock Mínimo"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditMinStockModalOpen(false);
                setEditingMinStock(null);
                setEditMinStockForm({ new_min: '' });
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveMinStock}
              data-modal-default="true"
            >
              Guardar
            </button>
          </>
        }
      >
        {editingMinStock && (
          <>
            <div className="input-group">
              <label className="input-label">Pieza</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingMinStock.pieceCode}</strong> - {editingMinStock.pieceName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Ubicación</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                {editingMinStock.locationName}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Mínimo actual</label>
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong>{editingMinStock.currentMin}</strong> unidades
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Nuevo mínimo *</label>
              <input
                type="number"
                min="0"
                step="1"
                className="input"
                value={editMinStockForm.new_min}
                onChange={(e) => setEditMinStockForm({ ...editMinStockForm, new_min: e.target.value })}
                placeholder={`Actual: ${editingMinStock.currentMin}`}
                required
                autoFocus
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Aplica al stock mínimo de la ubicación seleccionada
              </small>
            </div>

            {!isAdmin && !hasPermission('STOCK', 'adjust_stock') && (
              <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 59, 48, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 59, 48, 0.3)' }}>
                <strong style={{ color: 'var(--error)' }}>Advertencia:</strong>
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Solo los administradores pueden modificar mínimos.
                </p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Modal de Impresión */}
      <Modal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        title="Imprimir Lista de Stock"
        footer={
          <button className="btn btn-secondary" onClick={() => setIsPrintModalOpen(false)}>
            Cancelar
          </button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Selecciona el tipo de impresión que deseas:
          </p>
          <button
            className="btn btn-primary"
            onClick={() => handlePrintStock(true)}
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Printer size={20} />
            Imprimir con Cantidades
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePrintStock(false)}
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Printer size={20} />
            Imprimir Plantilla Vacía
          </button>
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            <strong>Nota:</strong> Se imprimirán todas las piezas de la lista completa {selectedPieceTypes.length > 0 && selectedPieceTypes.length < 4 ? `(filtradas por: ${selectedPieceTypes.join(', ')})` : '(sin filtros)'} con las columnas visibles actuales.
          </div>
        </div>
      </Modal>
    </div>
  );
}

